import { useState, useEffect } from 'react';
import { User, Key, Globe, Shield, Bot, Bell, Save, Loader2 } from 'lucide-react';
import { getSettings, updateSetting, getUsers, createUser } from '@/api';
import { useAuthStore } from '@/store';
import { User as UserType } from '@/types';
import { useToast } from '@/components/ui/Toaster';
import clsx from 'clsx';

const settingsSections = [
  { id: 'general', label: 'General', icon: Globe },
  { id: 'ai', label: 'AI Features', icon: Bot },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'team', label: 'Team', icon: User },
] as const;

export default function Settings() {
  const { user } = useAuthStore();
  const { addToast } = useToast();
  const [activeSection, setActiveSection] = useState<'general' | 'ai' | 'notifications' | 'security' | 'team'>('general');
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'staff' as UserType['role'], domains: '' });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [settingsRes, usersRes] = await Promise.all([getSettings(), getUsers()]);
        if (settingsRes.ok) setSettings(settingsRes.data || {});
        if (usersRes.ok) setUsers(usersRes.data || []);
      } catch (e) {
        addToast('Failed to load settings', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [addToast]);

  const handleSave = async (key: string, value: string) => {
    setSaving(true);
    try {
      const result = await updateSetting(key, value);
      if (result.ok) {
        setSettings((prev) => ({ ...prev, [key]: value }));
        addToast('Setting saved', 'success');
      } else {
        addToast(result.error || 'Failed to save', 'error');
      }
    } catch (e) {
      addToast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const result = await createUser({
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        domains: JSON.parse(newUser.domains || '[]'),
      });
      if (result.ok) {
        addToast('User added', 'success');
        setNewUser({ name: '', email: '', role: 'staff', domains: '' });
        const usersRes = await getUsers();
        if (usersRes.ok) setUsers(usersRes.data || []);
      } else {
        addToast(result.error || 'Failed to add user', 'error');
      }
    } catch (e) {
      addToast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'general':
        return (
          <div className="space-y-6">
            <h3 className="font-medium text-gray-900">General Settings</h3>
            <div className="card p-5 space-y-4">
              <SettingToggle
                label="Auto-reply enabled"
                description="Automatically reply to incoming emails"
                key="auto_reply_enabled"
                value={settings.auto_reply_enabled}
                onChange={handleSave}
              />
              <SettingInput
                label="Default forward emails"
                description="Comma-separated emails to forward inbound messages to"
                key="default_forward_emails"
                value={settings.default_forward_emails}
                onChange={handleSave}
              />
              <SettingInput
                label="Spam threshold"
                description="Score above which emails are marked as spam (0-1)"
                key="spam_threshold"
                value={settings.spam_threshold}
                type="number"
                onChange={handleSave}
              />
            </div>
          </div>
        );
      case 'ai':
        return (
          <div className="space-y-6">
            <h3 className="font-medium text-gray-900">AI Features</h3>
            <div className="card p-5 space-y-4">
              <SettingToggle
                label="AI Classification"
                description="Automatically classify incoming emails"
                key="ai_classification_enabled"
                value={settings.ai_classification_enabled}
                onChange={handleSave}
              />
              <SettingToggle
                label="AI Summarization"
                description="Generate summaries for email threads"
                key="ai_summarization_enabled"
                value={settings.ai_summarization_enabled}
                onChange={handleSave}
              />
            </div>
          </div>
        );
      case 'notifications':
        return (
          <div className="space-y-6">
            <h3 className="font-medium text-gray-900">Notifications</h3>
            <div className="card p-5 space-y-4">
              <p className="text-sm text-gray-500">Notification settings coming soon</p>
            </div>
          </div>
        );
      case 'security':
        return (
          <div className="space-y-6">
            <h3 className="font-medium text-gray-900">Security</h3>
            <div className="card p-5 space-y-4">
              <p className="text-sm text-gray-500">Security is managed via Cloudflare Access. Configure policies in Cloudflare Zero Trust dashboard.</p>
            </div>
          </div>
        );
      case 'team':
        return (
          <div className="space-y-6">
            <h3 className="font-medium text-gray-900">Team Members</h3>
            <div className="card p-5">
              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-3">Add Team Member</h4>
                <form onSubmit={handleAddUser} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <input name="name" placeholder="Name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="input" required />
                  <input name="email" type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="input" required />
                  <select name="role" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value as UserType['role'] })} className="input">
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                    <option value="support">Support</option>
                  </select>
                  <input name="domains" placeholder='Domains JSON: ["chiefops.co.za"]' value={newUser.domains} onChange={(e) => setNewUser({ ...newUser, domains: e.target.value })} className="input" />
                  <button type="submit" disabled={saving} className="btn-primary md:col-span-4">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add User'}
                  </button>
                </form>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Current Team</h4>
                <div className="space-y-2">
                  {users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-signal-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-signal-700">{u.name.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{u.name}</p>
                          <p className="text-sm text-gray-500">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 capitalize">{u.role}</span>
                        <span className="text-xs text-gray-500">{u.domains?.join(', ')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Configure your Signal Inbox</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:w-52 flex-shrink-0">
          <nav className="bg-white rounded-xl border border-gray-200 p-2" role="navigation" aria-label="Settings sections">
            {settingsSections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id as any)}
                  className={clsx(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
                    activeSection === section.id
                      ? 'bg-signal-50 text-signal-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {section.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          {renderSection()}
        </div>
      </div>
    </div>
  );
}

// ─── Setting Components ──────────────────────────────────────

function SettingToggle({ label, description, key, value, onChange }: {
  label: string; description: string; key: string; value: string; onChange: (key: string, value: string) => void;
}) {
  const checked = value === 'true';
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <button
        onClick={() => onChange(key, checked ? 'false' : 'true')}
        className={clsx('relative w-11 h-6 rounded-full transition-colors', checked ? 'bg-signal-600' : 'bg-gray-300')}
        role="switch"
        aria-checked={checked}
      >
        <span className={clsx('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-5' : 'translate-x-0.5')} />
      </button>
    </div>
  );
}

function SettingInput({ label, description, key, value, onChange, type = 'text' }: {
  label: string; description: string; key: string; value: string; onChange: (key: string, value: string) => void; type?: string;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(key, e.target.value)} className="input" />
      <p className="text-sm text-gray-500 mt-1">{description}</p>
    </div>
  );
}